import React, { useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useMyProfile, queryKeys } from '@/hooks/api';
import { useAppStore } from '@/store';
import { Button, LoadingScreen } from '@/components/ui';
import { TelegramUtils } from '@/utils/telegram';
import { PaymentHelper } from '@/utils/payments';
import { api } from '@/api';
import { cn, getBadgeLevelColor, capitalizeBadgeLevel } from '@/utils';
import type { VerificationStatusResponse } from '@/types';

const SkillsVerificationScreen: React.FC = () => {
  const { setActiveScreen, setITQuestParams, setUser } = useAppStore();
  const { data: profile, isLoading: profileLoading } = useMyProfile();
  const queryClient = useQueryClient();
  const [verificationStatus, setVerificationStatus] = useState<VerificationStatusResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [processingPayment, setProcessingPayment] = useState(false);

  useEffect(() => {
    TelegramUtils.configureBackButton(true, () => {
      setActiveScreen('profile');
    });
    TelegramUtils.configureMainButton({ visible: false });
  }, [setActiveScreen]);

  useEffect(() => {
    loadVerificationStatus();
  }, []);

  const loadVerificationStatus = async () => {
    try {
      const status = await api.getVerificationStatus();
      setVerificationStatus(status);
      console.log('📊 Verification status:', status);
    } catch (error) {
      console.error('Failed to load verification status:', error);
      await TelegramUtils.showAlert('Не удалось загрузить статус верификации');
    } finally {
      setLoading(false);
    }
  };

  const handleRetakeTest = async () => {
    if (!profile || !verificationStatus) return;

    // Check if need to pay
    if (verificationStatus.total_attempts >= 2 || !verificationStatus.can_free_retry) {
      await handlePaidRetake();
      return;
    }

    // Free retry available
    startTest();
  };

  const handlePaidRetake = async () => {
    if (!verificationStatus) return;

    const confirmed = await TelegramUtils.showConfirm(
      `Пройти тест заново за ${verificationStatus.retry_price}⭐️?`
    );

    if (!confirmed) return;

    setProcessingPayment(true);
    TelegramUtils.haptic.light();

    try {
      // Purchase with Telegram Stars
      const success = await PaymentHelper.purchaseWithStars('test_retry');

      if (success) {
        // Reload verification status
        await loadVerificationStatus();
        
        // Reload profile
        await queryClient.invalidateQueries({ queryKey: queryKeys.myProfile });
        
        TelegramUtils.haptic.success();
        await TelegramUtils.showAlert('Оплата успешна! Можно проходить тест.');
        
        // Start test
        startTest();
      }
    } catch (error) {
      console.error('Payment failed:', error);
      TelegramUtils.haptic.error();
    } finally {
      setProcessingPayment(false);
    }
  };

  const startTest = () => {
    if (!profile) return;

    // Get current specialization from profile
    const specializationId = profile.specializations?.[0]?.id;
    const declaredLevel = profile.level?.toLowerCase() || 'senior';

    if (!specializationId) {
      TelegramUtils.showAlert('Специализация не найдена. Обновите специальность.');
      return;
    }

    console.log('🚀 Starting test with specialization:', {
      id: specializationId,
      name: profile.specializations?.[0]?.name,
      declaredLevel,
    });

    // Set params for ITQuestScreen
    setITQuestParams({
      mode: 'retest',
      specializationId,
      declaredLevel,
    });

    // Navigate to IT Quest
    setActiveScreen('it-quest');
  };

  const handleUpdateSpecialization = async () => {
    if (!verificationStatus) return;

    if (!verificationStatus.can_free_retry) {
      // Need to pay first
      const confirmed = await TelegramUtils.showConfirm(
        `Для обновления специальности нужно заново пройти тест.\n\nСтоимость: ${verificationStatus.retry_price}⭐️\n\nПродолжить?`
      );

      if (!confirmed) return;

      setProcessingPayment(true);

      try {
        const success = await PaymentHelper.purchaseWithStars('test_retry');

        if (success) {
          await loadVerificationStatus();
          await queryClient.invalidateQueries({ queryKey: queryKeys.myProfile });
          TelegramUtils.haptic.success();
          setActiveScreen('update-specialization');
        }
      } catch (error) {
        console.error('Payment failed:', error);
        TelegramUtils.haptic.error();
      } finally {
        setProcessingPayment(false);
      }
    } else {
      // Free retry available - just confirm
      const confirmed = await TelegramUtils.showConfirm(
        'После обновления специальности нужно будет заново пройти тест верификации.\n\nПродолжить?'
      );

      if (confirmed) {
        setActiveScreen('update-specialization');
      }
    }
  };

  if (profileLoading || loading) {
    return <LoadingScreen message="Загрузка информации..." />;
  }

  if (!profile || !verificationStatus) {
    return (
      <div className="min-h-screen bg-tg-bg flex items-center justify-center p-6">
        <p className="text-white">Ошибка загрузки данных</p>
      </div>
    );
  }

  const specialization = profile.specializations?.[0];
  const techStack = profile.tech_stack || [];
  
  console.log('📊 Profile data:', {
    specializations: profile.specializations,
    tech_stack: profile.tech_stack,
  });

  return (
    <div className="min-h-screen bg-tg-bg safe-area-y p-6">
      {/* Header */}
      <div className="mb-6">
        <button
          onClick={() => setActiveScreen('profile')}
          className="flex items-center gap-2 text-white hover:opacity-80 transition-opacity mb-4"
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          <span className="font-medium">Назад</span>
        </button>
        <h1 className="text-3xl font-bold text-white">Навыки и верификация</h1>
      </div>

      {/* Current Skills */}
      <div className="bg-white/5 border border-white/10 rounded-2xl p-5 mb-6">
        <h2 className="text-lg font-bold text-white mb-4">Текущие навыки</h2>
        
        {specialization && (
          <div className="mb-4">
            <p className="text-sm text-neutral-400 mb-1">Специализация</p>
            <p className="text-white text-lg font-semibold">{specialization.name}</p>
          </div>
        )}

        {techStack.length > 0 && (
          <div>
            <p className="text-sm text-neutral-400 mb-2">Tech Stack</p>
            <div className="flex flex-wrap gap-2">
              {techStack.map((tech: any) => (
                <span
                  key={tech.id}
                  className="px-3 py-1.5 bg-white/10 border border-white/20 rounded-full text-sm text-white"
                >
                  {tech.name}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Verification Status */}
      <div className="bg-white/5 border border-white/10 rounded-2xl p-5 mb-6">
        <h2 className="text-lg font-bold text-white mb-4">Статус верификации</h2>
        
        <div className="space-y-3">
          {/* Score */}
          {verificationStatus.verification_score !== null && verificationStatus.verification_score !== undefined && (
            <div className="flex items-center justify-between">
              <span className="text-neutral-400">Балл</span>
              <span className="text-white text-xl font-bold">
                {verificationStatus.verification_score}
              </span>
            </div>
          )}

          {/* Badge Level */}
          {verificationStatus.verification_badge_level && (
            <div className="flex items-center justify-between">
              <span className="text-neutral-400">Уровень</span>
              <span className={cn(
                'px-3 py-1 rounded-full font-semibold',
                getBadgeLevelColor(verificationStatus.verification_badge_level)
              )}>
                {capitalizeBadgeLevel(verificationStatus.verification_badge_level)}
              </span>
            </div>
          )}

          {/* Attempts */}
          <div className="flex items-center justify-between">
            <span className="text-neutral-400">Попытки</span>
            <span className="text-white">
              {verificationStatus.total_attempts} из 2
            </span>
          </div>

          {/* Free retries remaining */}
          {verificationStatus.can_free_retry && verificationStatus.total_attempts < 2 && (
            <div className="mt-3 bg-success-500/10 border border-success-500/30 rounded-xl p-3">
              <p className="text-sm text-success-400">
                ✨ У вас есть бесплатная попытка повторного прохождения
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Actions */}
      <div className="space-y-3">
        {/* Retake Test Button */}
        <Button
          onClick={handleRetakeTest}
          disabled={processingPayment}
          loading={processingPayment}
          className="w-full h-14 bg-white text-black rounded-2xl font-bold hover:bg-neutral-100 transition-all active:scale-95"
        >
          {verificationStatus.can_free_retry && verificationStatus.total_attempts < 2
            ? 'Пройти тест заново (бесплатно)'
            : `Пройти тест заново за ${verificationStatus.retry_price}⭐️`
          }
        </Button>

        {/* Update Specialization Button */}
        <Button
          onClick={handleUpdateSpecialization}
          disabled={processingPayment}
          className="w-full h-14 bg-white/10 border border-white/20 rounded-2xl text-white font-semibold hover:bg-white/20 transition-all active:scale-95"
        >
          Обновить специальность
        </Button>
      </div>
    </div>
  );
};

export default SkillsVerificationScreen;

